<template>
  <div class="package-table">
    <div class="smeta-action-bar tw-flex tw-items-center tw-justify-between tw-mb-4 tw-flex-wrap tw-gap-2">
      <div class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
        <div v-if="floorOptions.length" style="min-width: 180px;">
          <q-select
            v-model="selectedFloor"
            :options="floorOptions"
            label="Этаж"
            dense
            outlined
            emit-value
            map-options
            clearable
            options-dense
          />
        </div>
      </div>
      <div
        v-if="!!filteredData?.length"
        class="tw-flex tw-items-center tw-gap-4"
        style="font-weight: 700; font-size: 16px;"
      >
        <div class="tw-flex tw-flex-col tw-items-end">
          <span style="font-size: 11px; color: #888; font-weight: 500;">с НДС</span>
          <span><Amount :value="amount" :round="2" /> тг.</span>
        </div>
        <div style="width: 1px; height: 32px; background: #ddd;" />
        <div class="tw-flex tw-flex-col tw-items-end">
          <span style="font-size: 11px; color: #888; font-weight: 500;">без НДС</span>
          <span><Amount :value="amountWithoutNds" :round="2" /> тг.</span>
        </div>
      </div>
    </div>

    <q-linear-progress v-if="isLoading" indeterminate />

    <div v-if="!isLoading && !filteredData.length" class="tw-text-gray-400 tw-text-sm tw-py-8 tw-text-center">
      Нет данных
    </div>

    <div v-else style="position: relative;">
      <CustomTable
        :rowsData="filteredData"
        :customHeader="true"
        :custom-body="true"
        :small="true"
        :stickyHeader="true"
        :height="400"
      >
        <template #customHeader>
          <q-tr>
            <q-th align="center" class="th-main">Подъезд<br/>Этаж</q-th>
            <q-th align="left" class="th-main">Работа</q-th>
            <q-th align="right" class="th-smr"><div class="th-group-label">СМР</div>Объем</q-th>
            <q-th align="right" class="th-smr split-header">
              <div class="split-header-top">Цена с НДС</div>
              <div class="split-header-bottom">Цена без НДС</div>
            </q-th>
            <q-th align="right" class="th-smr split-header">
              <div class="split-header-top">Сумма с НДС</div>
              <div class="split-header-bottom">Сумма без НДС</div>
            </q-th>
            <q-th align="left" class="th-mat"><div class="th-group-label">Материал</div>Наименование</q-th>
            <q-th align="right" class="th-mat">Кол-во</q-th>
            <q-th align="right" class="th-mat" style="font-size:11px; color:#888;">Норма</q-th>
            <q-th align="right" class="th-mat split-header">
              <div class="split-header-top">Цена с НДС</div>
              <div class="split-header-bottom">Цена без НДС</div>
            </q-th>
            <q-th align="right" class="th-mat split-header">
              <div class="split-header-top">Сумма с НДС</div>
              <div class="split-header-bottom">Сумма без НДС</div>
            </q-th>
            <q-th align="right" class="th-main split-header">
              <div class="split-header-top">Итого с НДС</div>
              <div class="split-header-bottom">Итого без НДС</div>
            </q-th>
          </q-tr>
        </template>

        <template #customBody="props">
          <q-tr v-if="props.row.isGroupHeader" class="group-header-row">
            <q-td colspan="11" class="group-header-cell">
              {{ props.row.work_set_check_group_name || 'Без группы' }}
            </q-td>
          </q-tr>

          <q-tr v-else>
            <!-- Подъезд / Этаж -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="center"
              vertical-align="center"
            >
              <div class="entrance-cell">
                <span class="entrance-label">П.{{ props.row.entrance }}</span>
                <span class="floor-label">Эт.{{ props.row.level_num }}</span>
              </div>
            </custom-table-cell>

            <!-- Конструктив -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="left"
              vertical-align="center"
            >
              <div class="work-cell-wrap">
                <span class="cell-text-block">
                  <span>{{ props.row.wname }}</span>
                  <span class="unit-inline">{{ props.row.wunt }}</span>
                </span>
                <span v-if="props.row.work_rowspan > 1" class="multi-material-badge">
                  {{ props.row.work_rowspan }} мат.
                </span>
              </div>
            </custom-table-cell>

            <!-- СМР: Кол-во -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
            >
              {{ props.row.wqty }}
            </custom-table-cell>

            <!-- СМР: Цена (с НДС / без НДС) -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
            >
              <div class="split-data">
                <div class="split-data-top"><Amount :value="props.row.wprc" :round="2" /></div>
                <div class="split-data-bottom"><Amount :value="props.row.wprc_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>

            <!-- СМР: Сумма (с НДС / без НДС) -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
            >
              <div class="split-data">
                <div class="split-data-top">
                  <Amount :value="props.row.wsum" :round="2" />
                  <span v-if="props.row.nds_percent_work_set != null" class="nds-inline">
                    {{ Number(props.row.nds_percent_work_set) }}%
                  </span>
                </div>
                <div class="split-data-bottom"><Amount :value="props.row.wsum_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>

            <!-- Материал: Наименование -->
            <custom-table-cell align="left" vertical-align="center" class="material-name-cell">
              <template v-if="props.row.material_id">
                <span class="cell-text-block">
                  <span>{{ props.row.mname }}</span>
                  <span class="unit-inline">{{ props.row.munt }}</span>
                </span>
              </template>
              <span v-else class="tw-text-gray-300 tw-text-xs">—</span>
            </custom-table-cell>

            <!-- Материал: Кол-во -->
            <custom-table-cell align="right" vertical-align="center">
              <span v-if="props.row.material_id">{{ props.row.mqty }}</span>
            </custom-table-cell>

            <!-- Материал: Норма расхода -->
            <custom-table-cell align="right" vertical-align="center">
              <span
                v-if="props.row.norm_ratio > 0"
                class="norm-cell"
                :title="`Норма: ${props.row.norm_ratio.toFixed(4)} на ед. работы`"
              >
                {{ props.row.norm_ratio.toFixed(3) }}
              </span>
            </custom-table-cell>

            <!-- Материал: Цена (с НДС / без НДС) -->
            <custom-table-cell align="right" vertical-align="center">
              <div class="split-data" v-if="props.row.material_id">
                <div class="split-data-top"><Amount :value="props.row.mprc" :round="2" /></div>
                <div class="split-data-bottom"><Amount :value="props.row.mprc_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>

            <!-- Материал: Сумма (с НДС / без НДС) -->
            <custom-table-cell align="right" vertical-align="center">
              <div class="split-data" v-if="props.row.material_id">
                <div class="split-data-top">
                  <Amount :value="props.row.msum" :round="2" />
                  <span v-if="props.row.nds_percent_material != null" class="nds-inline">
                    {{ Number(props.row.nds_percent_material) }}%
                  </span>
                </div>
                <div class="split-data-bottom"><Amount :value="props.row.msum_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>

            <!-- Итого (с НДС / без НДС) -->
            <custom-table-cell
              v-if="props.row.work_rowspan"
              :rowspan="props.row.work_rowspan"
              align="right"
              vertical-align="center"
            >
              <div class="split-data">
                <div class="split-data-top">
                  <Amount :value="props.row.ttl" :round="2" />
                </div>
                <div class="split-data-bottom"><Amount :value="props.row.ttl_no_nds" :round="2" /></div>
              </div>
            </custom-table-cell>
          </q-tr>
        </template>
      </CustomTable>
      <q-inner-loading :showing="isLoading" label="Загрузка..." />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Amount from '@/components/common/Amount.vue';
import CustomTable from '@/components/common/CustomTable';
import CustomTableCell from '@/components/common/CustomTable/custom-table-cell';
import { getProjectSmetaNew } from '@/views/ProjectsEntry/services';

const props = defineProps({
  params: { type: Object, default: () => ({}) },
  activeData: { type: Object, default: null },
});

const isLoading = ref(false);
const data = ref([]);
const selectedFloor = ref(null);

const projectId = computed(() => {
  const id = props.params?.project_id;
  return id ? Number(id) : null;
});

const projectEntranceId = computed(() => {
  const id = props.params?.project_entrance_id;
  return id ? Number(id) : null;
});

const floorOptions = computed(() => {
  const set = new Set();
  data.value.forEach((r) => {
    if (!r.isGroupHeader && r.level_num !== undefined && r.level_num !== null) {
      set.add(Number(r.level_num));
    }
  });
  return Array.from(set)
    .sort((a, b) => a - b)
    .map((v) => ({ label: `Этаж ${v}`, value: v }));
});

const filteredData = computed(() => {
  if (selectedFloor.value === null || selectedFloor.value === undefined || selectedFloor.value === '') {
    return data.value;
  }
  const target = Number(selectedFloor.value);
  const result = [];
  let pendingHeader = null;

  for (const r of data.value) {
    if (r.isGroupHeader) {
      if (Number(r.level_num) === target) {
        pendingHeader = r;
      } else {
        pendingHeader = null;
      }
      continue;
    }
    if (Number(r.level_num) !== target) continue;
    if (pendingHeader) {
      result.push(pendingHeader);
      pendingHeader = null;
    }
    result.push(r);
  }
  return result;
});

const amount = computed(() => filteredData.value.reduce((acc, item) => acc + (item.ttl || 0), 0));
const amountWithoutNds = computed(() => filteredData.value.reduce((acc, item) => acc + (item.ttl_no_nds || 0), 0));

const loadData = async () => {
  if (!projectId.value) {
    data.value = [];
    return;
  }
  isLoading.value = true;
  const rows = await getProjectSmetaNew(projectId.value, projectEntranceId.value, 'contractor', null);
  data.value = rows || [];
  selectedFloor.value = null;
  isLoading.value = false;
};

watch(
  () => [props.params?.project_id, props.params?.project_entrance_id],
  () => { loadData(); },
  { immediate: true },
);

watch(
  () => props.activeData?.floor_map_id,
  (newFloorMapId) => {
    if (!newFloorMapId) {
      selectedFloor.value = null;
      return;
    }
    const matchRow = data.value.find(r => !r.isGroupHeader && r.floor_map_id === newFloorMapId);
    if (matchRow) {
      selectedFloor.value = Number(matchRow.level_num);
    }
  },
);
</script>

<style scoped lang="scss">
.package-table {
  margin-top: 0;
}

.smeta-action-bar {
  padding: 8px 0;
}

.group-header-row {
  .group-header-cell {
    background-color: #e8eaf6;
    font-weight: 700;
    font-size: 13px;
    padding: 6px 12px !important;
    color: #3949ab;
    border-top: 2px solid #c5cae9;
  }
}

.work-cell-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cell-text-block {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.unit-inline {
  font-size: 10px;
  color: #9e9e9e;
  font-style: italic;
  line-height: 1.2;
  margin-top: 1px;
}

.entrance-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1.3;

  .entrance-label {
    font-weight: 600;
    font-size: 12px;
  }
  .floor-label {
    font-size: 11px;
    color: #666;
  }
}

.norm-cell {
  font-size: 11px;
  color: #888;
  font-style: italic;
}

.nds-inline {
  display: block;
  font-size: 10px;
  color: #9e9e9e;
  font-style: italic;
  font-weight: normal;
  line-height: 1.1;
  margin-top: 1px;
}

.multi-material-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 2px 6px;
  border-radius: 8px;
  background-color: #eef2ff;
  color: #4f46e5;
  font-size: 10px;
  font-weight: 600;
}

.split-data {
  display: flex;
  flex-direction: column;

  .split-data-top {
    padding: 2px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .split-data-bottom {
    padding: 2px 0;
    color: #555;
  }
}

:deep(.th-main) {
  font-weight: 700 !important;
  font-size: 12px;
}

:deep(.th-smr) {
  font-weight: 600 !important;
  font-size: 11px;
  background-color: #f8f9fa !important;
}

:deep(.th-mat) {
  font-weight: 600 !important;
  font-size: 11px;
  background-color: #f0f4ff !important;
}

.th-group-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #7986cb;
  line-height: 1;
  margin-bottom: 2px;
}

.split-header {
  padding: 0 !important;

  .split-header-top {
    padding: 4px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }

  .split-header-bottom {
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
}

:deep(.material-name-cell) {
  border-left: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>
